import { useState, FormEvent } from 'react';
import { Button, Input } from '../ui';
import { useAuth } from '../../stores/auth.store';
import { resendConfirmationCode } from '../../services/auth.service';

interface ConfirmSignUpFormProps {
  email: string;
  onSuccess?: () => void;
  onBack?: () => void;
}

export function ConfirmSignUpForm({
  email,
  onSuccess,
  onBack,
}: ConfirmSignUpFormProps) {
  const { confirmSignUp, isLoading, error, clearError } = useAuth();
  const [code, setCode] = useState('');
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setResendMessage(null);

    const success = await confirmSignUp(email, code);
    if (success) {
      onSuccess?.();
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setResendMessage(null);
    clearError();

    try {
      await resendConfirmationCode(email);
      setResendMessage('A new verification code has been sent to your email.');
    } catch {
      setResendMessage('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-6">
        <p className="text-slate-300">We&apos;ve sent a verification code to</p>
        <p className="text-white font-medium">{email}</p>
      </div>

      <Input
        label="Verification Code"
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="000000"
        required
        fullWidth
        disabled={isLoading}
        className="text-center text-2xl tracking-widest font-mono"
        helperText="Enter the 6-digit code from your email"
      />

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {resendMessage && (
        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
          <p className="text-sm text-green-400">{resendMessage}</p>
        </div>
      )}

      <Button
        type="submit"
        fullWidth
        isLoading={isLoading}
        disabled={code.length !== 6}
      >
        Verify Email
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onBack}
          className="text-slate-400 hover:text-white transition-colors"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleResendCode}
          disabled={isResending}
          className="text-purple-400 hover:text-purple-300 transition-colors disabled:opacity-50"
        >
          {isResending ? 'Sending...' : 'Resend code'}
        </button>
      </div>

      {/* Dev hint */}
      {import.meta.env.DEV && (
        <div className="mt-4 p-3 rounded-lg bg-slate-700/50 text-xs text-slate-400">
          <p className="font-medium text-slate-300 mb-1">Development Mode:</p>
          <p>Any 6-digit code (e.g., 123456) will be accepted.</p>
        </div>
      )}
    </form>
  );
}
